import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PromptContext } from '../../context/PromptContext';
import { deleteSavedPrompts } from '../../services/savedPrompts';
import { generateChatSessionId } from '../../utils/chat';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';

import { Button } from 'react-bootstrap';
import SaveTopicIcon from '../../assets/icon-google-topic.svg?react';
import DeleteIcon from '../../assets/icon-google-delete.svg?react';
import TickIcon from '../../assets/icon-google-tick.svg?react';
import { LoadingContext } from '../../context/LoadingContext';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import useElysiaStore from '@/app/elysia-store';

export const SavedPromptButton = (props) => {
  const { topic, index, setSavedTopics, handleClose } = props;
  const navigate = useNavigate();
  const [deletedPromptId, setDeletedPromptId] = useState<number | null>(null);
  const [showBadge, setShowBadge] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isLoading } = useContext(LoadingContext);
  const { setProcessed } = useContext(PromptContext);

  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setIsPromptSetManually = useElysiaStore.getState().setIsPromptSetManually;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;

  const startNewSavedPromptChat = (promptValue) => {
    useElysiaStore.setState({ isStreamingStopped: false });
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    if (chatAgent !== 'app-agent') {
      setChatAgent?.('Generic');
    }
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    if (!isAppPreview) {
      navigate('/');
    }
    setIsChatActive?.(false);
    setPromptInput(promptValue);
    setIsPromptSetManually?.(true);
    setSessionPath?.('');
    setProcessed?.(false);
    setIsRestrictedChatSessionStatus?.('');
    handleClose();
  };

  const handleDeleteSavedPrompts = async (promptId: number) => {
    try {
      const response = await deleteSavedPrompts(promptId);
      if (response) {
        setDeletedPromptId(promptId);
        setShowBadge(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          setDeletedPromptId(null);
          setShowBadge(false);
          setSavedTopics((prevTopics) =>
            prevTopics.filter((topic) => topic.prompt_id !== promptId)
          );
        }, 2000);
      }
    } catch (err) {
      console.error('error while deleting the saved prompt', err);
    }
  };

  return (
    <>
      <Button
        disabled={isLoading}
        className="saved-prompt-topic"
        key={index}
        onClick={() => {
          startNewSavedPromptChat(topic.prompt);
        }}
      >
        <div className="d-flex mb-2 saved-prompt-text-container">
          <SaveTopicIcon className="save-topic-icon" />
          <div className="ms-2 saved-topic-text">{topic?.prompt}</div>
        </div>
        <div className="delete-saved-topic">
          <OverlayTooltip
            tooltipText="Delete"
            buttonContent={<DeleteIcon />}
            ariaLabel="delete saved prompt"
            testId="delete-saved-prompt-button"
            tooltipClassName="save-prompt-tooltip"
            buttonClassName="delete-saved-topic-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSavedPrompts(topic.prompt_id);
            }}
            placement="bottom"
            disabled={deletedPromptId === topic.prompt_id}
          />
        </div>
        {showBadge && (
          <span className="history-badge-delete-prompt-button">
            <TickIcon />
            <span>Deleted</span>
          </span>
        )}
      </Button>
    </>
  );
};
